import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitCompareArrows, Loader2, ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { listFiles, runCompare } from "@/lib/api";
import ComparisonResult from "@/components/ComparisonResult";

export default function ComparePage() {
  const [files, setFiles] = useState([]);
  const [baseline, setBaseline] = useState("");
  const [current, setCurrent] = useState("");
  const [busy, setBusy] = useState(false);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    listFiles().then(setFiles);
  }, []);

  const run = async () => {
    if (!baseline || !current) {
      toast.error("Select both a baseline and a current file");
      return;
    }
    setBusy(true);
    setComparison(null);
    try {
      const res = await runCompare(baseline, current);
      setComparison(res);
      toast.success(`Comparison complete · ${res.summary.total_changes} changes`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Comparison failed");
    } finally {
      setBusy(false);
    }
  };

  const fileSelect = (value, setter, testid, exclude) => (
    <Select value={value} onValueChange={setter}>
      <SelectTrigger data-testid={testid} className="rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm">
        <SelectValue placeholder="Select file…" />
      </SelectTrigger>
      <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
        {files
          .filter((f) => f.id !== exclude)
          .map((f) => (
            <SelectItem key={f.id} value={f.id} className="font-mono text-xs">
              {f.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Compare</h1>
          <p className="font-mono text-[11px] text-zinc-500">
            Diff a current document against a reference baseline
          </p>
        </div>
      </header>

      <div className="p-8 space-y-6">
        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-4 items-end">
            <div className="space-y-1.5">
              <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                Baseline / Reference
              </label>
              {fileSelect(baseline, setBaseline, "baseline-select", current)}
            </div>
            <div className="hidden md:flex items-center justify-center pb-2 text-zinc-600">
              <ArrowRight className="w-5 h-5" />
            </div>
            <div className="space-y-1.5">
              <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                Current / As-Constructed
              </label>
              {fileSelect(current, setCurrent, "current-select", baseline)}
            </div>
            <Button
              data-testid="run-compare-btn"
              onClick={run}
              disabled={busy}
              className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <GitCompareArrows className="w-4 h-4 mr-2" />
              )}
              Run Diff
            </Button>
          </div>
          {files.length < 2 && (
            <p className="font-mono text-[11px] text-amber-400/80 mt-3">
              You need at least 2 stored documents. Load the sample dataset from the Library.
            </p>
          )}
        </div>

        {comparison ? (
          <ComparisonResult comparison={comparison} />
        ) : (
          <div className="grid-texture border border-zinc-800 rounded-sm py-20 flex flex-col items-center text-center">
            <GitCompareArrows className="w-10 h-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-500">
              Pick two documents and run the diff to see asset-level changes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
